import { Suspense, useEffect, useState } from 'react';

import { useSplitTestBranch } from '@api/lendingAPI/lapiHooks';
import {
    TFormalOffer,
    TFormalOffers
} from '@helpers/FormalOffers/formalOffers.types';
import { getFormalOffersLatestOfferConfiguration } from '@helpers/FormalOffers/getFormalOffersLatestOfferConfiguration';
import { getFormalOffersMaxAvailableCash } from '@helpers/FormalOffers/getFormalOffersMaxAvailableCash';
import { getInterestOnlyPeriodFromVariationTrees } from '@helpers/FormalOffers/getInterestOnlyPeriodFromVariationTrees';
import { getIsInterestOnlyOffer } from '@helpers/FormalOffers/getIsInterestOnlyOffer';
import { getTimeOutDate } from '@helpers/FormalOffers/getTimeOutDate';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconUnfoldHorizontal } from '@iwoca/icons';
import {
    fetchGetFormalOffers,
    fetchGetFormalOffersLatestOfferConfiguration,
    fetchPutFormalOffersCreditFacilityConfiguration
} from '@iwoca/lapi-client/edge';
import { Button, Label } from '@iwoca/orion';
import { useQuery } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';

import { ApprovalHeader } from './ApprovalHeader/ApprovalHeader';
import { BenefitsList } from './BenefitsList/BenefitsList';
import { ComparisonAndRepaymentModal } from './ComparisonAndRepaymentModal/ComparisonAndRepaymentModal';
import { FlexiApprovedViewSkeleton } from './FlexiApprovedViewSkeleton';
import { OfferChoiceButtons } from './OfferChoiceButtons/OfferChoiceButtons';
import { useMediaQuery } from '../../../../../customHooks/useMediaQuery';
import { ErrorBoundary } from '../../../../../errorBoundary/ErrorBoundary';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../../../../modals/fallbackErrorModal/FallbackErrorModal';
import { TrustPilot } from '../../../../TrustPilot/TrustPilot';
import { ChooseAmountInputWithSlider } from '../../../../ui/ChooseAmountInputWithSlider/ChooseAmountInputWithSlider';
import { isSelectedAmountValid } from '../../../../ui/ChooseAmountInputWithSlider/isSelectedAmountValid';
import { getMinAvailableCash } from '../../../MyOffers.helpers';
import { NeedHelpSection } from '../NeedHelpSection/NeedHelpSection';

type FlexiApprovedViewProps = {
    onSelectedOfferChange: (offer: TFormalOffer | undefined) => void;
    onDrawdownAmountChange: (amount: number) => void;
    onIsSingleOfferChange: (isSingle: boolean) => void;
};

export const FlexiApprovedView = (props: FlexiApprovedViewProps) => {
    return (
        <ErrorBoundary
            fallback={(error) => <FallbackErrorModal error={error} />}
        >
            <Suspense fallback={<FlexiApprovedViewSkeleton />}>
                <FlexiApprovedViewComponent {...props} />
            </Suspense>
        </ErrorBoundary>
    );
};

const getSelectedOffer = (formalOffers: TFormalOffers) => {
    if (formalOffers.length === 1) {
        return formalOffers[0];
    }
    return undefined;
};

const FlexiApprovedViewComponent = ({
    onSelectedOfferChange,
    onDrawdownAmountChange,
    onIsSingleOfferChange
}: FlexiApprovedViewProps) => {
    const [
        comparisonAndRepaymentModalOpen,
        setComparisonAndRepaymentModalOpen
    ] = useState(false);
    const stateKey = useStateKey();
    const isDesktop = useMediaQuery('(min-width: 768px)');
    const { data: getFormalOffersResponse } = useQuery({
        queryKey: ['formal_offers', stateKey],
        queryFn: () => fetchGetFormalOffers({ stateKey }),
        suspense: true
    });

    const formalOffers = getFormalOffersResponse!.data.formal_offers;
    const variationTrees =
        getFormalOffersResponse!.data.formal_offer_variation_relationship_trees;
    const nonInterestOnlyOffers = formalOffers.filter(
        (offer) => !getIsInterestOnlyOffer(offer)
    );
    const maxCashAmount = getFormalOffersMaxAvailableCash(formalOffers);
    const timeoutDate = getTimeOutDate(formalOffers);

    const {
        data: latestOfferConfiguration,
        refetch: refetchLatestOfferConfiguration
    } = useQuery({
        queryKey: ['fetchGetFormalOffersLatestOfferConfiguration', stateKey],
        queryFn: () =>
            fetchGetFormalOffersLatestOfferConfiguration({ stateKey }),
        select: (data) => data.data.latest_offer_configuration,
        suspense: true
    });

    const { previouslySelectedFormalOffer, previouslySelectedAmount } =
        getFormalOffersLatestOfferConfiguration({
            latestOfferConfiguration,
            formalOffers
        });

    const initialDrawdownAmount =
        previouslySelectedAmount ?? getMinAvailableCash(formalOffers);
    const [drawdownAmount, setDrawdownAmount] = useState<number>(
        initialDrawdownAmount
    );

    const { branch: interestOnlySplitTest } = useSplitTestBranch({
        test: 'new_customer_interest_only_add_on_test_2025_q2',
        suspense: true
    });

    const isInterstitialPage = interestOnlySplitTest === 'INTERSTITIAL_PAGE';
    const visibleOffers = isInterstitialPage
        ? nonInterestOnlyOffers
        : formalOffers;
    const interestOnlyPillPeriods = isInterstitialPage
        ? getInterestOnlyPeriodFromVariationTrees(variationTrees, formalOffers)
        : {};

    const initialSelectedOffer =
        previouslySelectedFormalOffer ?? getSelectedOffer(visibleOffers);
    const [selectedOffer, setSelectedOffer] = useState<
        TFormalOffer | undefined
    >(initialSelectedOffer);

    const isSingleOffer =
        visibleOffers.length === 1 &&
        selectedOffer?.offer_id === visibleOffers[0]?.offer_id;

    useEffect(() => {
        onSelectedOfferChange(initialSelectedOffer);
        onDrawdownAmountChange(initialDrawdownAmount);
        onIsSingleOfferChange(isSingleOffer);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateFormalOfferConfiguration = (
        offer: TFormalOffer | undefined,
        amount: number
    ) => {
        if (!offer || !isSelectedAmountValid(amount, offer)) {
            return;
        }

        fetchPutFormalOffersCreditFacilityConfiguration({
            stateKey,
            offerId: offer.offer_id!,
            body: { data: { cash_amount: amount, step: 'offer' } }
        }).then(() => refetchLatestOfferConfiguration());
    };

    const updateDrawdownAmount = (
        amount: number,
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setDrawdownAmount(amount);
        onDrawdownAmountChange(amount);
        updateFormalOfferConfiguration(selectedOffer, amount);
        tracking.ampli.drawdownAmountUpdated({
            amount_value: amount,
            input_source: event.target.type === 'range' ? 'slider' : 'input',
            'Offered max amount': maxCashAmount
        });
    };

    const hasComparableOffersWithValidAmount =
        nonInterestOnlyOffers.length > 1 &&
        isSelectedAmountValid(drawdownAmount, selectedOffer ?? null);

    const updateSelectedOffer = (offer: TFormalOffer) => {
        tracking.ampli.formalOfferSelected({
            formal_offer_id: offer.offer_id!,
            duration: getDurationInMonths(offer.duration!)
        });
        setSelectedOffer(offer);
        onSelectedOfferChange(offer);
        updateFormalOfferConfiguration(offer, drawdownAmount);
    };

    return (
        <div>
            <ApprovalHeader
                approvalStatusTimeOut={timeoutDate}
                maxCashAmount={maxCashAmount}
            />

            <BenefitsList />

            <div className="px-l pb-2xl min-sm:px-xl">
                <p className="text-2xl font-bold">
                    <FormattedMessage
                        id="_.flexi_approved.title"
                        defaultMessage="Let’s make your first withdrawal"
                    />
                </p>
                <ChooseAmountInputWithSlider
                    label={
                        <Label htmlFor="choose-amount">
                            <FormattedMessage
                                id="_.flexi_approved.label"
                                defaultMessage="How much would you like to withdraw today?"
                            />
                        </Label>
                    }
                    selectedAmount={drawdownAmount}
                    onChange={updateDrawdownAmount}
                    maxAmount={maxCashAmount}
                />

                {visibleOffers.length > 1 && (
                    <OfferChoiceButtons
                        formalOffers={visibleOffers}
                        selectedOfferId={selectedOffer?.offer_id}
                        drawdownAmount={drawdownAmount}
                        updateSelectedOffer={updateSelectedOffer}
                        interestOnlyPillPeriods={interestOnlyPillPeriods}
                    />
                )}

                {hasComparableOffersWithValidAmount && (
                    <Button
                        variant="tertiary"
                        className="mt-s"
                        onClick={() => {
                            tracking.ampli.compareOffersTableOpened();
                            setComparisonAndRepaymentModalOpen(true);
                        }}
                    >
                        <IconUnfoldHorizontal />
                        <FormattedMessage
                            id="_.flexi_approved.compare_loans_button"
                            defaultMessage="Compare these options"
                        />
                    </Button>
                )}

                {!isDesktop && (
                    <div className="mt-2xl">
                        <NeedHelpSection variant="offers-mobile" />
                    </div>
                )}

                <div className="mt-2xl pb-m">
                    <TrustPilot />
                </div>
            </div>

            <ComparisonAndRepaymentModal
                isModalOpen={comparisonAndRepaymentModalOpen}
                onClose={() => setComparisonAndRepaymentModalOpen(false)}
                formalOffers={nonInterestOnlyOffers}
                drawdownAmount={drawdownAmount}
                selectedOffer={selectedOffer!}
            />
        </div>
    );
};
